import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbPct} from '@features/admin-panel/helpers/admin-dashboard-format';
import {adbCategoryAxis, adbChartTheme, adbGrid, adbLegend, adbRedrawOnThemeChange, adbTooltip, adbValueAxis} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {AdminDashboardQueryParams, AdminDashboardWidgetState} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardUsageActivity} from '@features/admin-panel/models/admin-dashboard-usage.models';
import {AdminDashboardReportBlock, AdminDashboardReportProvider} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const usageActivityCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

const day = (iso: string): string => formatDate(iso, 'd MMM', 'en-US', 'UTC');
const int = (value: number): string => formatNumber(value ?? 0, 'en-US', '1.0-0');
const known = (value: number | null | undefined): boolean => value !== null && value !== undefined;

/**
 * "Projects, scenarios & entities" → "Active vs total" (ITP-E-214-05, Plan ITP-P-50 story 11): per day of the period,
 * the share of projects opened and of scenarios worked on in the last 30 days, with the latest counts in the header.
 * Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-usage-activity',
  templateUrl: './admin-dashboard-usage-activity.component.html',
  styleUrls: ['./admin-dashboard-usage-activity.component.scss']
})
export class AdminDashboardUsageActivityComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly cardTitle = 'Active vs total';
  readonly description = 'Share of projects opened and scenarios worked on in the last 30 days, per day.';
  readonly emptyTitle = 'No projects or scenarios in this view';
  readonly failedTitle = 'Couldn\'t load active vs total';

  data: AdminDashboardUsageActivity | null = null;
  /** False until the first response, so the empty state never flashes. */
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('platform-usage', this.host.nativeElement, this);
  }

  /** The chart container exists only while there is something to draw. */
  @ViewChild('chart') set chartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = usageActivityCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.reportRegistry.unregister(this);
  }

  /** No response, or neither a project nor a scenario counted (both totals unknown or 0). */
  get isEmpty(): boolean {
    return !this.data || (!this.data.projectsTotal && !this.data.scenariosTotal);
  }

  /**
   * "1,250 of 4,000 projects opened · 30 of 95 scenarios worked on in the last 30 days"; a side whose total is unknown
   * is left out; '' when both are.
   */
  get numbersText(): string {
    const data = this.data;
    if (!data) {
      return '';
    }
    const parts: string[] = [];
    if (known(data.projectsTotal)) {
      parts.push(`${int(data.projectsActive)} of ${countOf(data.projectsTotal, 'project')} opened`);
    }
    if (known(data.scenariosTotal)) {
      parts.push(`${int(data.scenariosActive)} of ${countOf(data.scenariosTotal, 'scenario')} worked on`);
    }
    return parts.length ? `${parts.join(' · ')} in the last 30 days` : '';
  }

  /** "Collecting history — trend available from 3 Oct 2026" while history is shorter than the period; else ''. */
  get historyText(): string {
    const from = this.data?.historyFrom;
    return from ? `Collecting history — trend available from ${formatDate(from, 'd MMM y', 'en-US', 'UTC')}` : '';
  }

  /** "7 Oct<br/>Projects: 31%<br/>Scenarios: 32%"; a series without that day is left out. */
  tooltipText(index: number): string {
    const projects = this.data?.projectsActiveShare?.[index];
    const scenarios = this.data?.scenariosActiveShare?.[index];
    const point = projects ?? scenarios;
    if (!point) {
      return '';
    }
    const lines = [day(point.date)];
    if (projects) {
      lines.push(`Projects: ${adbPct(projects.value)}`);
    }
    if (scenarios) {
      lines.push(`Scenarios: ${adbPct(scenarios.value)}`);
    }
    return lines.join('<br/>');
  }

  /** The card for the PDF report, built from what it shows now; never reloads. */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: this.cardTitle, text: this.failedTitle}];
    }
    if (this.isEmpty) {
      return [{kind: 'empty', title: this.cardTitle, text: this.emptyTitle}];
    }
    const block: AdminDashboardReportBlock = {
      kind: 'chart',
      title: this.cardTitle,
      subtitle: this.numbersText || this.description,
      image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
      widthPx: this.chart?.getWidth() ?? 0,
      heightPx: this.chart?.getHeight() ?? 0
    };
    if (this.historyText) {
      block.note = this.historyText;
    }
    return [block];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardUsageActivity>(
      'AdminDashboardUsageActivityQuery', this.params, 'Activity'
    ).subscribe({
      next: data => this.show(data ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(data: AdminDashboardUsageActivity | null, failed: boolean): void {
    this.data = data;
    this.failed = failed;
    this.loaded = true;
    this.render();
    this.report(failed ? 'failed' : this.isEmpty ? 'empty' : 'content');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  private render(): void {
    if (!this.chart || !this.data) {
      return;
    }
    const projects = this.data.projectsActiveShare ?? [];
    const scenarios = this.data.scenariosActiveShare ?? [];
    const days = projects.length >= scenarios.length ? projects : scenarios;
    // Two lines on the period's days: projects in blue, scenarios in purple, the axis in percent from 0. Colours come
    // from the card's --adb-* tokens.
    const t = adbChartTheme(this.host.nativeElement);
    const option = {
      textStyle: {fontFamily: t.font},
      grid: adbGrid(),
      legend: {...adbLegend(t), data: ['Projects', 'Scenarios']},
      xAxis: adbCategoryAxis(t, days.map(point => day(point.date)), {boundaryGap: false}),
      yAxis: adbValueAxis(t, (value: number) => adbPct(value), {min: 0}),
      tooltip: adbTooltip(t, {
        formatter: (points: any) => this.tooltipText((Array.isArray(points) ? points[0] : points)?.dataIndex ?? 0)
      }),
      series: [
        {
          name: 'Projects', type: 'line', data: projects.map(point => point.value), showSymbol: false,
          lineStyle: {width: 2, color: t.blue}, itemStyle: {color: t.blue}
        },
        {
          name: 'Scenarios', type: 'line', data: scenarios.map(point => point.value), showSymbol: false,
          lineStyle: {width: 2, color: t.purple}, itemStyle: {color: t.purple}
        }
      ]
    };
    this.chart.setOption(option, true);
  }
}
